import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import './AdminDashboardLayout.css';
import wsService from '../utils/websocketService';

function AdminDashboardLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [showLogoutWarning, setShowLogoutWarning] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [adminSession, setAdminSession] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const logoSrc = `${process.env.PUBLIC_URL || ''}/Png-01.png`;

    useEffect(() => {
        // Load admin session
        const session = localStorage.getItem('adminSession');
        if (session) {
            try {
                const parsedSession = JSON.parse(session);
                setAdminSession(parsedSession);

                // Redirect regular users (non-admins) to user dashboard
                const allowedRoles = ['admin', 'super_admin', 'sales', 'support', 'collection', 'branch', 'accounts'];
                if (!allowedRoles.includes(parsedSession.role)) {
                    console.warn('Non-admin user attempting to access admin portal');
                    navigate('/dashboard');
                    return;
                }
            } catch (e) {
                console.error('Error parsing admin session:', e);
            }
        } else {
            // No session, redirect to login
            navigate('/admin/login');
        }

        // Listen for session updates (e.g. permission changes)
        const handleAdminUpdate = (payload) => {
            if (payload && payload.adminId === JSON.parse(localStorage.getItem('adminSession'))?.userId) {
                console.log('🔄 Layout: Syncing session changes');
                const current = JSON.parse(localStorage.getItem('adminSession'));
                const updated = {
                    ...current,
                    permissions: payload.permissions || current.permissions,
                    role: payload.role || current.role,
                    level: payload.level || current.level,
                    name: payload.name || current.name
                };
                // Persist + sync so refreshed pages immediately reflect new permissions
                localStorage.setItem('adminSession', JSON.stringify(updated));
                setAdminSession(updated);
            }
        };

        wsService.on('admin.updated', handleAdminUpdate);
        return () => wsService.off('admin.updated', handleAdminUpdate);
    }, [navigate]);

    // Establish WebSocket for real-time permission sync (all admin roles)
    useEffect(() => {
        if (!adminSession?.userId) return;

        wsService.connect(adminSession.userId, true)
            .catch(err => console.error('WS connect error (layout):', err));

        return () => {
            try { wsService.disconnect(); } catch (e) { /* ignore */ }
        };
    }, [adminSession?.userId]);

    const handleLogout = () => {
        // Clear all session data
        localStorage.clear();
        sessionStorage.clear();

        // Clear browser history to prevent back button access
        window.history.pushState(null, '', '/admin/login');

        // Disable back button after logout
        window.onpopstate = function () {
            window.history.pushState(null, '', '/admin/login');
        };

        // Force page reload to clear any cached state and navigate to admin login
        window.location.replace('/admin/login');
    };

    const confirmLogout = () => {
        setShowLogoutWarning(false);
        handleLogout();
    };

    const cancelLogout = () => {
        setShowLogoutWarning(false);
    };

    const getRoleBadge = () => {
        if (!adminSession) return null;
        const role = adminSession.role;
        const roleLabels = {
            'super_admin': { label: 'Super Admin', class: 'super-admin' },
            'admin': { label: 'Admin', class: 'admin' },
            'sales': { label: 'Sales', class: 'sales' },
            'support': { label: 'Support', class: 'support' },
            'collection': { label: 'Collections', class: 'support' },
            'branch': { label: 'Branch', class: 'admin' },
            'accounts': { label: 'Accounts', class: 'sales' }
        };
        const config = roleLabels[role] || { label: 'Staff', class: 'admin' };
        return <span className={`role-badge ${config.class}`}>{config.label}</span>;
    };

    return (
        <div className="admin-dashboard-layout">
            {/* Sidebar Navigation */}
            <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="admin-sidebar-header" onClick={() => navigate('/admin/super-dashboard')}>
                    <img src={logoSrc} alt="E-Fin" className="admin-sidebar-logo-img" />
                    <div className="admin-sidebar-title">
                        <h2>E-Fin</h2>
                        <span>Admin Portal</span>
                    </div>
                </div>

                <div className="admin-sidebar-menu">
                    <div className="menu-section-label">Main Menu</div>
                    <button
                        className={`sidebar-menu-item ${location.search === '' || location.search.includes('tab=dashboard') ? 'active' : ''}`}
                        onClick={() => navigate('/admin/super-dashboard?tab=dashboard')}
                    >
                        <span className="menu-icon">🏠</span>
                        <span>Dashboard</span>
                    </button>

                    {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('analytics.view')) && (
                        <button
                            className={`sidebar-menu-item ${location.search.includes('tab=overview') ? 'active' : ''}`}
                            onClick={() => navigate('/admin/super-dashboard?tab=overview')}
                        >
                            <span className="menu-icon">📊</span>
                            <span>Overview</span>
                        </button>
                    )}

                    {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('analytics.view')) && (
                        <button
                            className={`sidebar-menu-item ${location.search.includes('tab=analytics') ? 'active' : ''}`}
                            onClick={() => navigate('/admin/super-dashboard?tab=analytics')}
                        >
                            <span className="menu-icon">📈</span>
                            <span>Analytics</span>
                        </button>
                    )}

                    {/* User Management */}
                    <div className="menu-section-label">Management</div>
                    {(adminSession?.role === 'super_admin') && (
                        <button
                            className={`sidebar-menu-item ${location.search.includes('tab=admins') ? 'active' : ''}`}
                            onClick={() => navigate('/admin/super-dashboard?tab=admins')}
                        >
                            <span className="menu-icon">🔐</span>
                            <span>Admins</span>
                        </button>
                    )}
                    {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('users.view_all')) && (
                        <button
                            className={`sidebar-menu-item ${location.search.includes('tab=users') ? 'active' : ''}`}
                            onClick={() => navigate('/admin/super-dashboard?tab=users')}
                        >
                            <span className="menu-icon">👥</span>
                            <span>Users</span>
                        </button>
                    )}
                    {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('kyc.manage')) && (
                        <button
                            className={`sidebar-menu-item ${location.search.includes('tab=kyc') ? 'active' : ''}`}
                            onClick={() => navigate('/admin/super-dashboard?tab=kyc')}
                        >
                            <span className="menu-icon">🆔</span>
                            <span>KYC Management</span>
                        </button>
                    )}
                    {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('loans.view')) && (
                        <button
                            className={`sidebar-menu-item ${location.search.includes('tab=loans') ? 'active' : ''}`}
                            onClick={() => navigate('/admin/super-dashboard?tab=loans')}
                        >
                            <span className="menu-icon">💰</span>
                            <span>Loans</span>
                        </button>
                    )}
                    {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('leads.view')) && (
                        <button
                            className={`sidebar-menu-item ${location.search.includes('tab=leads') ? 'active' : ''}`}
                            onClick={() => navigate('/admin/super-dashboard?tab=leads')}
                        >
                            <span className="menu-icon">🎯</span>
                            <span>Leads</span>
                        </button>
                    )}

                    {/* System & Configuration */}
                    {(adminSession?.role === 'super_admin' ||
                        ['permissions.manage', 'roles.manage', 'settings.manage', 'database.manage'].some(p => adminSession?.permissions?.includes(p))
                    ) && (
                            <>
                                <div className="menu-section-label">System</div>
                                {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('permissions.manage')) && (
                                    <button
                                        className={`sidebar-menu-item ${location.search.includes('tab=permissions') ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=permissions')}
                                    >
                                        <span className="menu-icon">🔑</span>
                                        <span>Permissions</span>
                                    </button>
                                )}
                                {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('roles.manage')) && (
                                    <button
                                        className={`sidebar-menu-item ${location.search.includes('tab=roles') ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=roles')}
                                    >
                                        <span className="menu-icon">👔</span>
                                        <span>Roles</span>
                                    </button>
                                )}
                                {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('settings.manage')) && (
                                    <button
                                        className={`sidebar-menu-item ${location.search.includes('tab=settings') ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=settings')}
                                    >
                                        <span className="menu-icon">⚙️</span>
                                        <span>Settings</span>
                                    </button>
                                )}
                                {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('database.manage')) && (
                                    <button
                                        className={`sidebar-menu-item ${location.search.includes('tab=database') ? 'active' : ''}`}
                                        onClick={() => navigate('/admin/super-dashboard?tab=database')}
                                    >
                                        <span className="menu-icon">💾</span>
                                        <span>Database</span>
                                    </button>
                                )}
                            </>
                        )}

                    {/* Monitoring */}
                    <div className="menu-section-label">Monitoring</div>
                    {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('activity.view')) && (
                        <button
                            className={`sidebar-menu-item ${location.search.includes('tab=activity') ? 'active' : ''}`}
                            onClick={() => navigate('/admin/super-dashboard?tab=activity')}
                        >
                            <span className="menu-icon">📡</span>
                            <span>Activity Monitor</span>
                        </button>
                    )}
                    {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('logs.view')) && (
                        <button
                            className={`sidebar-menu-item ${location.search.includes('tab=logs') ? 'active' : ''}`}
                            onClick={() => navigate('/admin/super-dashboard?tab=logs')}
                        >
                            <span className="menu-icon">📜</span>
                            <span>Audit Logs</span>
                        </button>
                    )}
                    {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('reports.view')) && (
                        <button
                            className={`sidebar-menu-item ${location.search.includes('tab=reports') ? 'active' : ''}`}
                            onClick={() => navigate('/admin/super-dashboard?tab=reports')}
                        >
                            <span className="menu-icon">📋</span>
                            <span>Reports</span>
                        </button>
                    )}

                    {/* Extras */}
                    <div className="menu-section-label">Support & Extras</div>
                    {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('tickets.manage')) && (
                        <button
                            className={`sidebar-menu-item ${location.search.includes('tab=support') ? 'active' : ''}`}
                            onClick={() => navigate('/admin/super-dashboard?tab=support')}
                        >
                            <span className="menu-icon">🎫</span>
                            <span>Support</span>
                        </button>
                    )}
                    {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('financials.view')) && (
                        <button
                            className={`sidebar-menu-item ${location.search.includes('tab=financials') ? 'active' : ''}`}
                            onClick={() => navigate('/admin/super-dashboard?tab=financials')}
                        >
                            <span className="menu-icon">💱</span>
                            <span>Financials</span>
                        </button>
                    )}
                    {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('payments.manage')) && (
                        <button
                            className={`sidebar-menu-item ${location.search.includes('tab=payments') ? 'active' : ''}`}
                            onClick={() => navigate('/admin/super-dashboard?tab=payments')}
                        >
                            <span className="menu-icon">💳</span>
                            <span>Payment Gateway</span>
                        </button>
                    )}
                    {(adminSession?.role === 'super_admin') && (
                        <>
                            {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('api.manage')) && (
                                <button
                                    className={`sidebar-menu-item ${location.search.includes('tab=api') ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=api')}
                                >
                                    <span className="menu-icon">🔌</span>
                                    <span>API Management</span>
                                </button>
                            )}
                            {(adminSession?.role === 'super_admin' || adminSession?.permissions?.includes('system.health')) && (
                                <button
                                    className={`sidebar-menu-item ${location.search.includes('tab=health') ? 'active' : ''}`}
                                    onClick={() => navigate('/admin/super-dashboard?tab=health')}
                                >
                                    <span className="menu-icon">📡</span>
                                    <span>System Health</span>
                                </button>
                            )}
                        </>
                    )}
                </div>

                <div className="admin-sidebar-footer">
                    <button className="sidebar-logout-btn" onClick={() => setShowLogoutWarning(true)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                            <polyline points="16 17 21 12 16 7"></polyline>
                            <line x1="21" y1="12" x2="9" y2="12"></line>
                        </svg>
                        {!sidebarCollapsed && 'Logout'}
                    </button>
                </div>

                {/* Sidebar Toggle Button */}
                <button
                    className="sidebar-toggle-btn"
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {sidebarCollapsed ? (
                            <path d="M9 18l6-6-6-6" />
                        ) : (
                            <path d="M15 18l-6-6 6-6" />
                        )}
                    </svg>
                </button>
            </aside>

            {/* Main Content Area */}
            <div className="admin-main-container">
                {/* Top Header */}
                <header className="admin-top-header">
                    <div className="header-left">
                        <h1 className="active-tab-title">
                            {location.search.includes('tab=dashboard') ? 'Dashboard' :
                                location.search.includes('tab=overview') ? 'Business Overview' :
                                    location.search.includes('tab=analytics') ? 'Analytics & Insights' :
                                        location.search.includes('tab=admins') ? 'Admin Management' :
                                            location.search.includes('tab=users') ? 'User Management' :
                                                location.search.includes('tab=kyc') ? 'KYC Central' :
                                                    location.search.includes('tab=permissions') ? 'Permissions Control' :
                                                        location.search.includes('tab=roles') ? 'Role Management' :
                                                            location.search.includes('tab=settings') ? 'System Settings' :
                                                                location.search.includes('tab=database') ? 'Database Management' :
                                                                    location.search.includes('tab=security') ? 'Security Center' :
                                                                        location.search.includes('tab=activity') ? 'Activity Monitor' :
                                                                            location.search.includes('tab=logs') ? 'Audit Logs' :
                                                                                location.search.includes('tab=reports') ? 'Reports & Analytics' :
                                                                                    location.search.includes('tab=templates') ? 'Templates' :
                                                                                        location.search.includes('tab=support') ? 'Support' :
                                                                                            location.search.includes('tab=financials') ? 'Financials' :
                                                                                                location.search.includes('tab=payments') ? 'Payment Gateway' :
                                                                                                    location.search.includes('tab=api') ? 'API Management' :
                                                                                                        location.search.includes('tab=notifications') ? 'Notifications' :
                                                                                                            location.search.includes('tab=health') ? 'System Health' :
                                                                                                                location.search.includes('tab=loans') ? 'Loan Management' : 'Dashboard'}
                        </h1>
                    </div>
                    <div className="header-right">
                        {getRoleBadge()}
                        <button
                            className="admin-nav-icon-btn"
                            title="Profile"
                            onClick={() => setShowProfile(!showProfile)}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                        </button>
                    </div>

                    {/* Profile Dropdown */}
                    {showProfile && adminSession && (
                        <div className="admin-dropdown-panel admin-profile-panel">
                            <div className="admin-profile-header">
                                <div className="admin-profile-avatar">
                                    {adminSession.name ? adminSession.name.split(' ').map(n => n[0]).join('') : 'AD'}
                                </div>
                                <div className="admin-profile-info">
                                    <h3>{adminSession.name || 'Admin'}</h3>
                                    <p>{adminSession.email}</p>
                                </div>
                            </div>
                            <div className="admin-profile-details">
                                <div className="admin-detail-row">
                                    <span className="admin-detail-label">Role</span>
                                    <span className="admin-detail-value">
                                    {adminSession.role === 'super_admin' ? 'Super Administrator' :
                                        adminSession.role === 'sales' ? 'Sales Representative' :
                                            adminSession.role === 'support' ? 'Support Executive' :
                                                adminSession.role === 'collection' ? 'Collections' :
                                                    adminSession.role === 'branch' ? 'Branch Manager' :
                                                        adminSession.role === 'accounts' ? 'Accounts Ops' : 'Administrator'}
                                    </span>
                                </div>
                                <div className="admin-detail-row">
                                    <span className="admin-detail-label">Permissions</span>
                                    <span className="admin-detail-value">
                                        {adminSession.role === 'super_admin' ? 'Full System Access' : `${adminSession.permissions?.length || 0} Features Active`}
                                    </span>
                                </div>
                            </div>
                            <div className="admin-profile-actions">
                                <button
                                    className="admin-profile-logout-btn"
                                    onClick={() => {
                                        setShowProfile(false);
                                        setShowLogoutWarning(true);
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                        <polyline points="16 17 21 12 16 7"></polyline>
                                        <line x1="21" y1="12" x2="9" y2="12"></line>
                                    </svg>
                                    Logout
                                </button>
                            </div>
                        </div>
                    )}
                </header>

                <main className="admin-dashboard-main">
                    <Outlet />
                </main>
            </div>

            {/* Logout Warning Modal */}
            {showLogoutWarning && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content">
                        <div className="admin-modal-icon warning">⚠️</div>
                        <h3>Logout from Admin Portal?</h3>
                        <p>You will be logged out and redirected to the admin login page.</p>
                        <div className="admin-modal-actions">
                            <button className="admin-modal-btn cancel" onClick={cancelLogout}>
                                Cancel
                            </button>
                            <button className="admin-modal-btn confirm" onClick={confirmLogout}>
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminDashboardLayout;
